import { Controller } from "@hotwired/stimulus"

// Three-way theme setting: system → light → dark. The chosen value is stamped
// on <html data-theme="..."> (see the inline head script that applies it
// before first paint) and persisted in localStorage.
export default class extends Controller {
  static targets = ["label"]

  static ORDER = ["system", "light", "dark"]

  connect() {
    // More than one toggle can be on the page — the sidebar has one and the
    // profile page has another, which is what makes the theme reachable on a
    // phone at all. Whichever is clicked, both must end up reporting the same
    // thing.
    this.sync = () => this.render()
    window.addEventListener("netrix:theme", this.sync)
    this.render()
  }

  cycle() {
    // Made a live region at the moment of the press, not on connect. The
    // label is the only thing that moves — nothing else on the page reports
    // where the cycle landed — and changing text inside an ordinary element
    // announces nothing.
    //
    // On the press rather than on connect because two of these can share a
    // page: the sidebar's and the profile page's own, both re-rendered by the
    // window event below. Live on connect, one press would say "Dark" twice.
    // The other copy updates silently, which is right — it is reporting, not
    // answering.
    if (this.hasLabelTarget) this.labelTarget.setAttribute("aria-live", "polite")

    const current = localStorage.getItem("netrix:theme") || "system"
    const order = this.constructor.ORDER
    const next = order[(order.indexOf(current) + 1) % order.length]

    localStorage.setItem("netrix:theme", next)
    this.apply(next)
    window.dispatchEvent(new CustomEvent("netrix:theme", { detail: { theme: next } }))
  }

  disconnect() {
    window.removeEventListener("netrix:theme", this.sync)
  }

  apply(theme) {
    if (theme === "system") {
      delete document.documentElement.dataset.theme
    } else {
      document.documentElement.dataset.theme = theme
    }
    // The palette changes in place, so the strip above the page has to as
    // well — otherwise it is left on the old colour and the mismatch this
    // fixes is back, one press later. The rule lives in the head script that
    // runs before first paint; this calls it rather than restating it.
    window.netrixPaintBrowserChrome?.(theme)
  }

  render() {
    if (this.hasLabelTarget) {
      const current = localStorage.getItem("netrix:theme") || "system"
      this.labelTarget.textContent =
        current.charAt(0).toUpperCase() + current.slice(1)
    }
  }
}
